import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Avatar } from '@boringer-avatars/react';
import { getUserName } from '../../../userLocalDetails.js';
import jeddleIcon from '../../../static/icons/jeddle_icon.png';
import { IbmplexsansNormalShark20px } from '../../../styledMixins';
import { MultiSubjectBadges } from '../MultiSubjectBadges';
import { useSubjects } from '../../../hooks/useSubjects';

const RightContainer = styled.div`
  display: ${(props) => (props.showActions ? 'block !important' : 'none')};
`;

const CardEntry = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 20px;
  position: relative;
  align-self: stretch;
  background-color: var(--white);
  border: 0px 0px 1px 0px;
  border-bottom: 1px solid var(--color-neutral-alpha-90, #c9c6cc80);
  &:hover ${RightContainer} {
    display: block;
  }
  ${(props) =>
    props.isHovered && !props.disableNavigation &&
    css`
      &:hover {
        background-color: #f2f1f380;
      }
      cursor: pointer;
    `};
  ${(props) =>
    props.isPopupMode &&
    css`
      cursor: pointer;
      &:hover {
        background-color: #f2f1f380;
      }
    `};
  ${(props) =>
    props.isSelected && props.isPopupMode &&
    css`
      background:rgb(212, 207, 227);
    `};
`;

const CardEntryHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  position: relative;
  align-self: stretch;
  width: 100%;
`;

const LeftContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: ${(props) => (!props.disableNavigation ? 'pointer' : 'default')};
  flex: 1;
  min-width: 0;
     
  &:hover {
    opacity: ${(props) => (!props.disableNavigation ? '0.8' : '1')};
  }
  
  ${(props) =>
    props.isPopupMode &&
    css`
      cursor: pointer;
      &:hover {
        opacity: 0.8;
      }
    `};
`;

const ClickableContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: ${(props) => (!props.isSystem && !props.disableNavigation ? 'pointer' : 'default')};
  flex: 1;
  min-width: 0;
  
  ${(props) =>
    props.isPopupMode && !props.disableNavigation &&
    css`
      cursor: pointer;
    `};
`;

const Title = styled.div`
  ${IbmplexsansNormalShark20px}
  position: relative;
  flex: 1;
  margin-top: -1px;
  letter-spacing: 0;
  line-height: normal;
`;

const TypeTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TypeText = styled.div`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #6B7280;
  margin-top: 2px;
`;

const SystemBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background: #F9F3FF;
  color: #50009D;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
`;

const PersonBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background: #F9F3FF;
  color: #50009D;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
`;

const JeddleIcon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
  data-checkbox-container: true;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #7200E0;
     
  &:hover {
    transform: scale(1.1);
  }
`;

const BadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const GenericCard = ({
  item,
  type,
  isSelected,
  onSelect,
  onEdit,
  showCheckbox = true,
  showActions = true,
  isSystem = false,
  rightComponent,
  onNavigate,
  onPreview,
  badgeConfig,
  disableNavigation = false,
  isShared = false,
  forceShowRightComponent = false
}) => {
  const history = useHistory();
  const currentUserName = getUserName();
  // Get the creator name from the item object, falling back to current user
  const name = item?.teacherName || item?.teacher?.name || item?.creatorName || currentUserName;
  const { rawSubjects: apiSubjects } = useSubjects();
  const metadata = item?.metadata || {};
  const itemSubjectValues = metadata?.subjects || [];
  const itemSubjectsMap = new Map();
  (metadata?.subjects || []).forEach((s) => {
    if (s && typeof s === 'object' && (s.subjectCode ?? s.code)) {
      itemSubjectsMap.set(s.subjectCode ?? s.code, s.subjectName ?? s.name ?? s.title);
    }
  });
  const rawYears = metadata?.years || [];
  const years = rawYears.length > 0 ? rawYears : ['Unassigned'];


  const subjects = useMemo(() => {
    if (!itemSubjectValues?.length) return ['Unassigned'];
    const codeToName = new Map();
    (apiSubjects || []).forEach((s) => {
      const code = s.subjectCode || s.code;
      const name = s.subjectName || s.title || s.name || '';
      if (code) codeToName.set(code, name || code);
      if (name) codeToName.set(name, name);
    });
    // First pass: resolve each subject to { code, name }
    const resolved = itemSubjectValues.map((v) => {
      const code = typeof v === 'object' && v != null ? (v.subjectCode ?? v.code) : (typeof v === 'string' ? v : null);
      const inlineName = typeof v === 'object' && v != null ? (v.subjectName ?? v.title ?? v.name ?? '') : '';
      const fromApiMap = code ? codeToName.get(code) : null;
      const fromItemMap = code ? itemSubjectsMap.get(code) : null;
      const mapped = fromApiMap || fromItemMap || inlineName || 'Unassigned';
      return { code, name: mapped };
    });
    // Detect duplicate names — show "Name (Code)" only for duplicates
    const nameCounts = new Map();
    resolved.forEach(({ name }) => nameCounts.set(name, (nameCounts.get(name) || 0) + 1));
    return resolved.map(({ code, name }) => {
      if (nameCounts.get(name) > 1 && code && code !== name) {
        return `${name} (${code})`;
      }
      return name;
    });
  }, [itemSubjectValues, apiSubjects]);

  const handleCardBodyClick = (e) => {
    e.stopPropagation();
    if (onPreview && !disableNavigation) {
      onPreview(item);
      return;
    }
    if (!isSystem && onNavigate) {
      onNavigate(item);
    }
  };

  const handleCardClick = (e) => {
    if (showCheckbox && !e.target.closest('button') && !e.target.closest('input') && !e.target.closest('[data-checkbox-container]')) {
      if (disableNavigation && onSelect) {
        onSelect();
      } else {
        handleCardBodyClick(e);
      }
    }
    else if (!showCheckbox && onSelect && !e.target.closest('button') && !e.target.closest('input')) {
      onSelect();
    }
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect();
    }
  };

  const handleNavigate = (e) => {
    e.stopPropagation();
    if (!isSystem && onNavigate) {
      onNavigate(item);
    }
  };

  const handlePopupClick = (e) => {
    if (!showCheckbox && onSelect) {
      e.preventDefault();
      e.stopPropagation();
      onSelect();
    }
  };

  return (
    <CardEntry
      isHovered={!isSystem ? true : false}
      isSelected={isSelected}
      onClick={handleCardClick}
      isPopupMode={!showCheckbox}
      disableNavigation={disableNavigation}
    >
      <CardEntryHeading>
        <LeftContainer>
          {showCheckbox && (
            <CheckboxContainer data-checkbox-container>
              <Checkbox
                type="checkbox"
                checked={isSelected}
                onChange={handleCheckboxChange}
              />
            </CheckboxContainer>
          )}

          <ClickableContainer
            onClick={showCheckbox ? handleCardBodyClick : undefined}
            isSystem={isSystem}
            isPopupMode={!showCheckbox}
            disableNavigation={disableNavigation}
          >
            <HeadingContainer
              isPopupMode={!showCheckbox}
              disableNavigation={disableNavigation}
            >
              <TypeTextContainer>
                <Title>
                  {item.title}
                </Title>
              </TypeTextContainer>
              <BadgesRow>
                <MultiSubjectBadges years={years} subjects={subjects} taskTypes={metadata?.taskTypes} responseTypes={metadata?.responseTypes} size="small" />
                {isSystem ? (
                  <SystemBadge>
                    <JeddleIcon
                      src={jeddleIcon}
                      alt="Jeddle"
                    />
                    Template
                  </SystemBadge>
                ) : (
                  <PersonBadge>
                    <Avatar
                      title={false}
                      size={16}
                      variant="beam"
                      name={name}
                      square={false}
                    />
                    {name}
                  </PersonBadge>
                )}
              </BadgesRow>
            </HeadingContainer>
          </ClickableContainer>
        </LeftContainer>
        <RightContainer showActions={forceShowRightComponent}>
          {rightComponent}
        </RightContainer>
      </CardEntryHeading>
    </CardEntry>
  );
};

export default React.memo(GenericCard); 